import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { GlassButton } from '../ui/GlassButton';
import { BrandingCapability } from '../../services/api';

interface BrandingCapabilityBannerProps {
    capability: BrandingCapability | null | undefined;
    onReconnect: () => void;
}

const REASON_COPY: Record<string, string> = {
    missing_scope: 'AIME needs permission to manage this Page\'s profile — reconnect Facebook to grant it.',
    no_page_token: 'This Page isn\'t connected with a usable access token — reconnect Facebook.',
    token_expired: 'This Page\'s connection has expired — reconnect Facebook to continue.',
    permission_denied: 'Facebook denied this request — reconnect Facebook or check your Page role.',
    graph_error: 'AIME couldn\'t reach Facebook to check permissions right now.',
    network_error: 'AIME couldn\'t reach Facebook to check permissions right now.',
    unsupported_platform: 'Branding push is only available for Facebook Pages.',
};

/**
 * Small reusable banner: renders nothing when can_push is true; otherwise
 * renders reason-specific copy plus a Reconnect Facebook button.
 * See specs/completed/facebook-page-branding-live-push.md
 * "New Components" for the contract.
 */
export const BrandingCapabilityBanner: React.FC<BrandingCapabilityBannerProps> = ({ capability, onReconnect }) => {
    if (!capability || capability.can_push) return null;

    const reason = capability.reason || 'graph_error';
    const copy = REASON_COPY[reason] || 'AIME can\'t push branding to this Page right now.';
    const showReconnect = reason !== 'unsupported_platform';

    return (
        <div className="branding-capability-banner">
            <AlertTriangle size={16} className="branding-capability-banner-icon" />
            <span className="branding-capability-banner-text">{copy}</span>
            {showReconnect && (
                <GlassButton variant="secondary" size="sm" onClick={onReconnect}>
                    Reconnect Facebook
                </GlassButton>
            )}
        </div>
    );
};

export default BrandingCapabilityBanner;
